/**
 * MarketingStrategyView — Sprint 3 · Project Symphony
 *
 * Executive Briefing that transforms the six commander answers into
 * a deterministic, reviewable Marketing Strategy. No AI is invoked.
 *
 * On mount:
 *   - If the blueprint is still ``draft``, we automatically call
 *     POST /compile so the owner is never dropped onto a blank screen.
 *   - When the blueprint is ``planned`` or ``approved``, we render
 *     the full briefing without recompiling.
 *
 * Product Principles compliance:
 *   §1  Speak business, not AI — never uses model / prompt / provider vocab.
 *   §5  Every screen answers "what next?" — Approve is the primary action.
 *   §7  Language is a feature — Approve is a business decision; asset
 *       generation is a separate, later action (Sprint 5).
 */
import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";

import { Button } from "@/shared/ui/button";
import { approveStrategy, compileStrategy, getStrategy } from "../api";
import { useSyncCampaignMission } from "@/entities/missions";
import { useCurrentWorkspace } from "@/entities/workspace";
import { useAuthStore } from "@/entities/auth";
import { ExecutiveSummary } from "./sections/ExecutiveSummary";
import { CampaignHealthCard } from "./sections/CampaignHealthCard";
import { MarketingStrategySection } from "./sections/MarketingStrategySection";
import { ContentStrategySection } from "./sections/ContentStrategySection";
import { AllocationMatrixSection } from "./sections/AllocationMatrixSection";
import { PublishingCadenceSection } from "./sections/PublishingCadenceSection";
import { ExplainMyStrategySection } from "./sections/ExplainMyStrategySection";
import { ApproveStrategyBar } from "./sections/ApproveStrategyBar";

export function MarketingStrategyView(): JSX.Element {
  const params = useParams<{ blueprintId: string }>();
  const blueprintId = params.blueprintId ?? "";
  const qc = useQueryClient();
  const navigate = useNavigate();

  // Milestone 37.2 · Campaign Focus Flow sync — keeps the resume tile
  // aligned whenever the founder lands on the strategy view.
  const workspace = useCurrentWorkspace();
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const syncMission = useSyncCampaignMission(workspace?.id ?? null, userId);
  useEffect(() => {
    if (!blueprintId || !workspace?.id) return;
    syncMission.mutate(blueprintId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blueprintId, workspace?.id]);

  const query = useQuery({
    queryKey: ["blueprint", blueprintId],
    queryFn: () => getStrategy(blueprintId),
    enabled: Boolean(blueprintId),
    retry: 1,
  });

  const compile = useMutation({
    mutationFn: () => compileStrategy(blueprintId),
    onSuccess: (res) => qc.setQueryData(["blueprint", blueprintId], res),
  });

  const approve = useMutation({
    mutationFn: () => approveStrategy(blueprintId),
    onSuccess: (res) => {
      qc.setQueryData(["blueprint", blueprintId], res);
      // Patch 34.1.12 — invalidate everything the post-approval
      // surfaces read from so Content Review / Vault / Publishing /
      // Dashboard reflect the new drafts without a hard refresh.
      void qc.invalidateQueries({ queryKey: ["campaign-packs"] });
      void qc.invalidateQueries({ queryKey: ["generated-assets"] });
      void qc.invalidateQueries({ queryKey: ["ai-assets"] });
      void qc.invalidateQueries({ queryKey: ["library"] });
      void qc.invalidateQueries({ queryKey: ["review-queue"] });
      void qc.invalidateQueries({ queryKey: ["vault"] });
      void qc.invalidateQueries({ queryKey: ["publishing"] });
      void qc.invalidateQueries({ queryKey: ["dashboard"] });
      // Milestone 37.2 · sync the Campaign mission after approval so
      // Executive Home's resume tile advances from plan → generate.
      syncMission.mutate(blueprintId);
      // After approval, navigate to the Campaign Room — the campaign's
      // new permanent home. Sprint 4 executive directive.
      navigate(`/campaigns/${blueprintId}/room`);
    },
  });

  // If the blueprint is a fresh draft, auto-compile so the owner
  // never lands on an empty screen.
  useEffect(() => {
    const bp = query.data?.blueprint;
    if (bp && bp.status === "draft" && !compile.isPending) {
      compile.mutate();
    }
  }, [query.data?.blueprint?.status]);

  if (query.isLoading || (query.data?.blueprint.status === "draft" && compile.isPending)) {
    return <PreparingScreen />;
  }

  if (query.isError) {
    const err = query.error as { status?: number; message?: string; correlationId?: string | null } | undefined;
    const status = err?.status ?? 0;
    const corrId = err?.correlationId ?? null;
    const isTransient = status === 0 || status >= 500;
    const isAuthGap = status === 401 || status === 404;
    return (
      <div
        className="mx-auto max-w-2xl px-6 py-24 text-center"
        data-testid="marketing-strategy-error"
      >
        <h2 className="text-2xl font-semibold mb-3">
          We couldn&apos;t load this campaign.
        </h2>
        <p className="text-muted-foreground mb-2">
          {isTransient
            ? "The server hit a snag. Your work is safe — try again in a moment."
            : isAuthGap
            ? "This campaign is no longer available in your workspace."
            : (err?.message ?? "An unexpected error occurred.")}
        </p>
        {corrId && (
          <p className="text-[11px] text-muted-foreground mb-6" data-testid="strategy-error-corr-id">
            Reference ID: <code>{corrId}</code>
          </p>
        )}
        <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
          <Button
            type="button"
            data-testid="strategy-error-retry"
            onClick={() => query.refetch()}
            disabled={query.isRefetching}
          >
            {query.isRefetching ? "Retrying…" : "Retry"}
          </Button>
          <Button
            type="button"
            variant="secondary"
            data-testid="strategy-error-dashboard"
            onClick={() => navigate("/home")}
          >
            Return to Dashboard
          </Button>
          <Button
            type="button"
            variant="ghost"
            data-testid="strategy-error-report"
            onClick={() => {
              const subject = encodeURIComponent(`Campaign load failure${corrId ? ` (ref ${corrId})` : ""}`);
              const body = encodeURIComponent(
                `Blueprint: ${blueprintId}\nStatus: ${status}\nMessage: ${err?.message ?? "n/a"}\nCorrelation: ${corrId ?? "n/a"}\n`,
              );
              window.location.href = `mailto:support@emergent.sh?subject=${subject}&body=${body}`;
            }}
          >
            Report Issue
          </Button>
        </div>
      </div>
    );
  }

  const bp = query.data?.blueprint;
  if (!bp || !bp.strategy) {
    return <PreparingScreen />;
  }

  const readOnly = bp.status !== "planned";

  return (
    <div
      className="mx-auto max-w-5xl px-6 pt-10 pb-32"
      data-testid="marketing-strategy-view"
    >
      <header className="mb-10">
        <p className="text-sm uppercase tracking-widest text-muted-foreground mb-2">
          Marketing Strategy
        </p>
        <h1 className="text-4xl sm:text-5xl font-serif tracking-tight leading-tight">
          {bp.commander_answers.promotion}
        </h1>
        <p className="text-xs text-muted-foreground mt-4" data-testid="strategy-blueprint-id">
          Campaign {bp.id}
        </p>
      </header>

      {bp.status === "approved" && (
        <div
          className="mb-10 rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-5 flex flex-col sm:flex-row sm:items-center gap-4"
          data-testid="strategy-open-room-banner"
        >
          <div className="flex-1">
            <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-300">
              Strategy approved.
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              Your campaign has moved to its Campaign Room — the operational
              headquarters for everything that happens next.
            </p>
          </div>
          <Button
            type="button"
            onClick={() => navigate(`/campaigns/${blueprintId}/room`)}
            data-testid="strategy-open-room-button"
          >
            Open Campaign Room
          </Button>
        </div>
      )}

      <div className="space-y-10">
        <ExecutiveSummary summary={bp.strategy.executive_summary} />
        <CampaignHealthCard health={bp.strategy.campaign_health} />
        <MarketingStrategySection
          strategy={bp.strategy}
          timeline={bp.timeline!}
        />
        <ContentStrategySection content={bp.content_strategy!} />
        <AllocationMatrixSection allocation={bp.allocation!} />
        <PublishingCadenceSection cadence={bp.cadence!} />
        <ExplainMyStrategySection items={bp.strategy.explain_my_strategy} />
      </div>

      <ApproveStrategyBar
        approved={bp.status === "approved"}
        approving={approve.isPending}
        readOnly={readOnly && bp.status !== "planned"}
        onApprove={() => approve.mutate()}
        // Patch 37.4.39 · Back returns the founder to the editable
        // wizard against the SAME blueprint so answers can be revised
        // without losing the row / mission / pack link.
        onBack={() => navigate(`/campaigns/new?resume=${blueprintId}`)}
      />
    </div>
  );
}

function PreparingScreen(): JSX.Element {
  return (
    <div
      className="mx-auto max-w-2xl px-6 pt-24 pb-24 text-center"
      data-testid="marketing-strategy-preparing"
    >
      <div className="flex items-center justify-center gap-2 text-muted-foreground mb-6">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        <span className="text-sm">Composing your marketing strategy…</span>
      </div>
      <blockquote className="text-2xl sm:text-3xl font-serif leading-snug tracking-tight text-foreground">
        &ldquo;I&apos;m building the plan now. A moment, please.&rdquo;
      </blockquote>
    </div>
  );
}
